import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { UserRequestProto, UserProto } from "build-ts/interface";
import { listUsers, createUser, updateUser, deleteUser } from "services/UserService";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import UserManagementPanel from "modules/eva/components/UserManagementPanel";

const UserManagement: React.FC = () => {
  const { defaultTimeZone } = useUser();

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const [users, setUsers] = useState<UserProto[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formIsAdmin, setFormIsAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingUser, setEditingUser] = useState<UserProto | null>(null);
  const [editName, setEditName] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listUsers());
    } catch (err) {
      showToast("Error loading users", err instanceof Error ? err.message : "Failed to load users.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ─── Create ──────────────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setFormName("");
    setFormEmail("");
    setFormPassword("");
    setFormIsAdmin(false);
    setShowPassword(false);
    onCreateOpen();
  };

  const handleCreate = async () => {
    if (!formName.trim() || !formEmail.trim() || !formPassword.trim()) return;
    setIsSubmitting(true);
    try {
      await createUser(
        UserRequestProto.fromPartial({
          name: formName.trim(),
          email: formEmail.trim(),
          password: formPassword,
          is_admin: formIsAdmin,
        })
      );
      showToast("User created successfully", "", "success");
      onCreateClose();
      fetchUsers();
    } catch (err) {
      showToast("Error creating user", err instanceof Error ? err.message : "Failed to create user.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Edit ─────────────────────────────────────────────────────────────────────

  const handleRowClick = (user: UserProto) => {
    setEditingUser(user);
    setEditName(user.header?.name ?? "");
    setEditIsAdmin(user.is_admin ?? false);
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    if (!editingUser || !editName.trim()) return;
    setIsSaving(true);
    try {
      await updateUser({
        ...editingUser,
        header: { ...editingUser.header, name: editName.trim() },
        is_admin: editIsAdmin,
      });
      showToast("User updated successfully", "", "success");
      onEditClose();
      fetchUsers();
    } catch (err) {
      showToast("Error updating user", err instanceof Error ? err.message : "Failed to update user.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────────

  const handleDeleteSelected = () => {
    if (selectedUsers.size !== 1) return;
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedUsers.size !== 1) return;
    setIsDeleting(true);
    const userId = Array.from(selectedUsers)[0];
    try {
      await deleteUser(userId);
      setUsers(prev => prev.filter(u => u.header?.id !== userId));
      setSelectedUsers(new Set());
      showToast("User deleted successfully", "", "success");
    } catch (err) {
      showToast("Error deleting user", err instanceof Error ? err.message : "Failed to delete user.", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const selectedUserName = useMemo(() => {
    if (selectedUsers.size !== 1) return "";
    const userId = Array.from(selectedUsers)[0];
    return users.find(u => u.header?.id === userId)?.header?.name ?? "this user";
  }, [selectedUsers, users]);

  return (
    <UserManagementPanel
      defaultTimeZone={defaultTimeZone}
      users={users}
      loading={loading}
      selectedUsers={selectedUsers}
      onSelectionChange={setSelectedUsers}
      onRowClick={handleRowClick}
      onNewUserClick={handleOpenCreate}
      onDeleteSelected={handleDeleteSelected}
      isCreateOpen={isCreateOpen}
      onCreateClose={onCreateClose}
      formName={formName}
      onFormNameChange={setFormName}
      formEmail={formEmail}
      onFormEmailChange={setFormEmail}
      formPassword={formPassword}
      onFormPasswordChange={setFormPassword}
      formIsAdmin={formIsAdmin}
      onFormIsAdminChange={setFormIsAdmin}
      showPassword={showPassword}
      onToggleShowPassword={() => setShowPassword(v => !v)}
      isSubmitting={isSubmitting}
      onCreateSubmit={handleCreate}
      isEditOpen={isEditOpen}
      onEditClose={onEditClose}
      editingUser={editingUser}
      editName={editName}
      onEditNameChange={setEditName}
      editIsAdmin={editIsAdmin}
      onEditIsAdminChange={setEditIsAdmin}
      isSaving={isSaving}
      onSaveEdit={handleSaveEdit}
      showDeleteConfirmation={showDeleteConfirmation}
      onCloseDeleteConfirmation={() => setShowDeleteConfirmation(false)}
      onConfirmDelete={handleConfirmDelete}
      isDeleting={isDeleting}
      selectedUserName={selectedUserName}
    />
  );
};

export default UserManagement;
